import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

function decodeToken(token) {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
      .join(""),
  );
  return JSON.parse(jsonPayload);
}

function formatDateTime(value) {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleString();
}

function formatMoney(value) {
  const amount = Number(value || 0);
  return `$${amount.toFixed(2)}`;
}

function ProductReturnsOpsConsole() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [statusFilter, setStatusFilter] = useState("");
  const [ownerRouteFilter, setOwnerRouteFilter] = useState("");
  const [search, setSearch] = useState("");

  const [cases, setCases] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [selectedCase, setSelectedCase] = useState(null);
  const [auditRows, setAuditRows] = useState([]);

  const [users, setUsers] = useState([]);

  const [assignment, setAssignment] = useState({
    assignedToUserId: "",
    assignedRoleCode: "R6",
    note: "",
  });
  const [requestInfoReason, setRequestInfoReason] = useState("");
  const [escalateReason, setEscalateReason] = useState("");
  const [decisionForm, setDecisionForm] = useState({
    decision: "approve",
    reason: "",
    amountApproved: "",
  });
  const [refundForm, setRefundForm] = useState({
    amount: "",
    simulate: true,
  });

  const tokenHeader = () => {
    const token = localStorage.getItem("ogestyle_token");
    return { Authorization: `Bearer ${token}` };
  };

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const decoded = decodeToken(token);
      const hasR6 =
        decoded.role === "R6" ||
        decoded.role === "portal_admin" ||
        (Array.isArray(decoded.roles) &&
          (decoded.roles.includes("R6") ||
            decoded.roles.includes("portal_admin")));

      if (!hasR6) {
        navigate("/portal-admin");
        return false;
      }

      return true;
    } catch (_err) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (ownerRouteFilter) params.set("ownerRoute", ownerRouteFilter);
      params.set("limit", "150");

      const response = await axios.get(
        `${API_BASE_URL}/api/product-returns/ops/cases?${params.toString()}`,
        { headers: tokenHeader() },
      );

      const rows = Array.isArray(response.data?.cases)
        ? response.data.cases
        : [];
      setCases(rows);

      if (rows.length && !selectedCaseId) {
        setSelectedCaseId(rows[0]._id);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Unable to load return cases.");
    } finally {
      setLoading(false);
    }
  }, [ownerRouteFilter, selectedCaseId, statusFilter]);

  const fetchCaseDetail = useCallback(async (caseId) => {
    if (!caseId) return;
    setError("");

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/product-returns/ops/cases/${caseId}`,
        { headers: tokenHeader() },
      );

      setSelectedCase(response.data?.case || null);
      setAuditRows(Array.isArray(response.data?.audit) ? response.data.audit : []);

      if (response.data?.case?.assignedToUserId) {
        setAssignment((prev) => ({
          ...prev,
          assignedToUserId: String(response.data.case.assignedToUserId),
          assignedRoleCode: response.data.case.assignedRoleCode || "R6",
        }));
      }

      const amount =
        Number(response.data?.case?.financial?.amountApproved || 0) > 0
          ? Number(response.data.case.financial.amountApproved)
          : Number(response.data?.case?.financial?.amountRequested || 0);

      setRefundForm((prev) => ({
        ...prev,
        amount: amount > 0 ? String(amount) : "",
      }));
    } catch (err) {
      setError(err.response?.data?.message || "Unable to load case detail.");
    }
  }, []);

  const fetchAssignableUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/users?limit=200`, {
        headers: tokenHeader(),
      });
      setUsers(Array.isArray(response.data?.users) ? response.data.users : []);
    } catch (_err) {
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    if (!checkPortalAdmin()) return;
    fetchCases();
    fetchAssignableUsers();
  }, [checkPortalAdmin, fetchCases, fetchAssignableUsers]);

  useEffect(() => {
    if (selectedCaseId) {
      fetchCaseDetail(selectedCaseId);
    } else {
      setSelectedCase(null);
      setAuditRows([]);
    }
  }, [fetchCaseDetail, selectedCaseId]);

  const visibleCases = useMemo(() => {
    if (!search.trim()) return cases;
    const term = search.trim().toLowerCase();
    return cases.filter((row) => {
      const caseNumber = String(row?.caseNumber || "").toLowerCase();
      const orderNumber = String(row?.orderNumber || "").toLowerCase();
      return caseNumber.includes(term) || orderNumber.includes(term);
    });
  }, [cases, search]);

  const runAction = async (action) => {
    if (!selectedCaseId) {
      setError("Select a case first.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await action();
      await fetchCases();
      await fetchCaseDetail(selectedCaseId);
      setMessage("Action completed successfully.");
    } catch (err) {
      setError(err.response?.data?.message || "Action failed.");
    } finally {
      setSaving(false);
    }
  };

  const selectedCaseAmount =
    Number(selectedCase?.financial?.amountApproved || 0) > 0
      ? Number(selectedCase?.financial?.amountApproved || 0)
      : Number(selectedCase?.financial?.amountRequested || 0);

  return (
    <PortalAdminLayout>
      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "24px" }}>
        <h1 style={{ margin: "0 0 8px" }}>Product Returns Console</h1>
        <p style={{ margin: "0 0 16px", color: "#64748b" }}>
          R6 queue for review, assignment, decisions, and refund execution.
        </p>

        {error && (
          <div style={{ marginBottom: "12px", background: "#fee2e2", border: "1px solid #fecaca", color: "#991b1b", padding: "10px", borderRadius: "8px" }}>
            {error}
          </div>
        )}
        {message && (
          <div style={{ marginBottom: "12px", background: "#ecfdf5", border: "1px solid #bbf7d0", color: "#166534", padding: "10px", borderRadius: "8px" }}>
            {message}
          </div>
        )}

        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ padding: "8px", borderRadius: "8px", border: "1px solid #cbd5e1" }}>
            <option value="">All statuses</option>
            <option value="requested">requested</option>
            <option value="awaiting_evidence">awaiting_evidence</option>
            <option value="under_review">under_review</option>
            <option value="seller_review">seller_review</option>
            <option value="seller_approved">seller_approved</option>
            <option value="platform_review">platform_review</option>
            <option value="approved_for_refund">approved_for_refund</option>
            <option value="denied">denied</option>
            <option value="refund_processed">refund_processed</option>
            <option value="closed">closed</option>
          </select>

          <select value={ownerRouteFilter} onChange={(e) => setOwnerRouteFilter(e.target.value)} style={{ padding: "8px", borderRadius: "8px", border: "1px solid #cbd5e1" }}>
            <option value="">All owner routes</option>
            <option value="R1">R1</option>
            <option value="R4">R4</option>
            <option value="R7">R7</option>
          </select>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search case # or order #"
            style={{ flex: 1, minWidth: "220px", padding: "8px", borderRadius: "8px", border: "1px solid #cbd5e1" }}
          />

          <button onClick={fetchCases} style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer" }}>
            Refresh
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: "14px", alignItems: "start" }}>
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "10px", padding: "10px", maxHeight: "78vh", overflow: "auto", background: "#fff" }}>
            {loading ? (
              <div style={{ color: "#64748b" }}>Loading queue...</div>
            ) : visibleCases.length === 0 ? (
              <div style={{ color: "#64748b" }}>No return cases found.</div>
            ) : (
              visibleCases.map((row) => (
                <button
                  key={row._id}
                  onClick={() => setSelectedCaseId(row._id)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    border: selectedCaseId === row._id ? "1px solid #3b82f6" : "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "10px",
                    marginBottom: "8px",
                    background: selectedCaseId === row._id ? "#eff6ff" : "#fff",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{row.caseNumber || "N/A"}</div>
                  <div style={{ fontSize: "12px", color: "#475569" }}>Order: {row.orderNumber || "N/A"}</div>
                  <div style={{ fontSize: "12px", color: "#475569" }}>Status: {row.status || "N/A"}</div>
                  <div style={{ fontSize: "12px", color: "#475569" }}>Requested: {formatMoney(row?.financial?.amountRequested)}</div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>{formatDateTime(row.requestedAt)}</div>
                </button>
              ))
            )}
          </div>

          <div style={{ border: "1px solid #e2e8f0", borderRadius: "10px", padding: "12px", background: "#fff" }}>
            {!selectedCase ? (
              <div style={{ color: "#64748b" }}>Select a case to view details and actions.</div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "10px", marginBottom: "12px" }}>
                  <div><strong>Case</strong><div>{selectedCase.caseNumber || "N/A"}</div></div>
                  <div><strong>Status</strong><div>{selectedCase.status || "N/A"}</div></div>
                  <div><strong>Owner Route</strong><div>{selectedCase.ownerRoute || "N/A"}</div></div>
                  <div><strong>Requested</strong><div>{formatMoney(selectedCase?.financial?.amountRequested)}</div></div>
                </div>

                <div style={{ marginBottom: "12px" }}>
                  <strong>Order:</strong> {selectedCase.orderNumber || "N/A"} | <strong>Customer:</strong> {selectedCase.customerEmail || "N/A"}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                  <div style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                    <div style={{ fontWeight: 700, marginBottom: "6px" }}>Governance</div>
                    <div style={{ fontSize: "13px", color: "#334155" }}>
                      <strong>Governance Status:</strong>{" "}
                      {selectedCase.governanceStatus || "none"}
                    </div>
                    <div style={{ fontSize: "13px", color: "#334155" }}>
                      <strong>Assigned Role:</strong>{" "}
                      {selectedCase.assignedRoleCode || "N/A"}
                    </div>
                    <div style={{ fontSize: "13px", color: "#334155" }}>
                      <strong>Assigned User Id:</strong>{" "}
                      {selectedCase.assignedToUserId ? String(selectedCase.assignedToUserId) : "N/A"}
                    </div>
                  </div>

                  <div style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                    <div style={{ fontWeight: 700, marginBottom: "6px" }}>Risk Flags</div>
                    {Object.entries(selectedCase.riskFlags || {}).length === 0 ? (
                      <div style={{ fontSize: "13px", color: "#64748b" }}>No risk flags</div>
                    ) : (
                      Object.entries(selectedCase.riskFlags || {}).map(([key, value]) => (
                        <div key={key} style={{ fontSize: "13px", color: "#334155" }}>
                          <strong>{key}:</strong> {value ? "true" : "false"}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                  <div style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#fff" }}>
                    <div style={{ fontWeight: 700, marginBottom: "6px" }}>Items</div>
                    {(selectedCase.items || []).length === 0 ? (
                      <div style={{ fontSize: "13px", color: "#64748b" }}>No items.</div>
                    ) : (
                      (selectedCase.items || []).map((item, index) => (
                        <div key={`${item.orderItemId || "item"}-${index}`} style={{ borderTop: "1px solid #e2e8f0", paddingTop: "6px", marginTop: "6px" }}>
                          <div style={{ fontSize: "13px", fontWeight: 600 }}>
                            {item.productName || "Item"}
                          </div>
                          <div style={{ fontSize: "12px", color: "#475569" }}>
                            Qty requested: {item.quantityRequested || item.quantity || 1} | Purchased: {item.quantityPurchased || "N/A"}
                          </div>
                          <div style={{ fontSize: "12px", color: "#475569" }}>
                            Unit price: {formatMoney(item.priceAtPurchase)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#fff" }}>
                    <div style={{ fontWeight: 700, marginBottom: "6px" }}>Evidence</div>
                    {(selectedCase.evidence || []).length === 0 ? (
                      <div style={{ fontSize: "13px", color: "#64748b" }}>No evidence uploaded.</div>
                    ) : (
                      (selectedCase.evidence || []).map((entry, index) => (
                        <div key={`${entry.url || "evidence"}-${index}`} style={{ borderTop: "1px solid #e2e8f0", paddingTop: "6px", marginTop: "6px" }}>
                          <div style={{ fontSize: "12px", color: "#334155" }}>
                            <strong>Type:</strong> {entry.type || "note"}
                          </div>
                          {entry.url ? (
                            <a href={entry.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "12px" }}>
                              Open evidence
                            </a>
                          ) : null}
                          {entry.note ? (
                            <div style={{ fontSize: "12px", color: "#475569" }}>{entry.note}</div>
                          ) : null}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                  <div style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                    <div style={{ fontWeight: 700, marginBottom: "6px" }}>Eligibility Snapshot</div>
                    <div style={{ fontSize: "13px", color: "#334155" }}>
                      <strong>Eligible:</strong>{" "}
                      {selectedCase.eligibilitySnapshot?.eligible ? "yes" : "no"}
                    </div>
                    <div style={{ fontSize: "13px", color: "#334155" }}>
                      <strong>Window:</strong>{" "}
                      {selectedCase.eligibilitySnapshot?.windowDays ?? "N/A"} day(s)
                    </div>
                    <div style={{ fontSize: "13px", color: "#334155" }}>
                      <strong>Policy Source:</strong>{" "}
                      {selectedCase.eligibilitySnapshot?.policySource || "N/A"}
                    </div>
                    <div style={{ fontSize: "13px", color: "#334155" }}>
                      <strong>Reason:</strong>{" "}
                      {selectedCase.eligibilitySnapshot?.reason || "N/A"}
                    </div>
                  </div>

                  <div style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                    <div style={{ fontWeight: 700, marginBottom: "6px" }}>Item Condition Attestation</div>
                    <div style={{ fontSize: "13px", color: "#334155" }}>
                      <strong>Used:</strong>{" "}
                      {selectedCase.conditionAttestation?.itemUsedStatus || "unknown"}
                    </div>
                    <div style={{ fontSize: "13px", color: "#334155" }}>
                      <strong>Package Opened:</strong>{" "}
                      {selectedCase.conditionAttestation?.packageOpenedStatus || "unknown"}
                    </div>
                    <div style={{ fontSize: "13px", color: "#334155" }}>
                      <strong>Damaged:</strong>{" "}
                      {selectedCase.conditionAttestation?.itemDamagedStatus || "unknown"}
                    </div>
                    <div style={{ fontSize: "13px", color: "#334155" }}>
                      <strong>Detail:</strong>{" "}
                      {selectedCase.conditionAttestation?.reasonDetail || "N/A"}
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: "12px", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                  <div style={{ fontWeight: 700, marginBottom: "6px" }}>Assignment</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: "8px", marginBottom: "8px" }}>
                    <select
                      value={assignment.assignedToUserId}
                      onChange={(e) => setAssignment((prev) => ({ ...prev, assignedToUserId: e.target.value }))}
                      style={{ padding: "8px", borderRadius: "8px", border: "1px solid #cbd5e1" }}
                    >
                      <option value="">Select user</option>
                      {users.map((u) => (
                        <option key={u._id} value={u._id}>
                          {(u.firstName || "") + " " + (u.lastName || "") || u.name || "User"} - {u.email}
                        </option>
                      ))}
                    </select>
                    <select
                      value={assignment.assignedRoleCode}
                      onChange={(e) => setAssignment((prev) => ({ ...prev, assignedRoleCode: e.target.value }))}
                      style={{ padding: "8px", borderRadius: "8px", border: "1px solid #cbd5e1" }}
                    >
                      <option value="R1">R1</option>
                      <option value="R2">R2</option>
                      <option value="R6">R6</option>
                      <option value="R8">R8</option>
                    </select>
                  </div>
                  <input
                    value={assignment.note}
                    onChange={(e) => setAssignment((prev) => ({ ...prev, note: e.target.value }))}
                    placeholder="Assignment note"
                    style={{ width: "100%", padding: "8px", borderRadius: "8px", border: "1px solid #cbd5e1", marginBottom: "8px", boxSizing: "border-box" }}
                  />
                  <button
                    disabled={saving || !assignment.assignedToUserId}
                    onClick={() =>
                      runAction(() =>
                        axios.put(
                          `${API_BASE_URL}/api/product-returns/ops/cases/${selectedCaseId}/assign`,
                          assignment,
                          { headers: tokenHeader() },
                        ),
                      )
                    }
                    style={{ padding: "8px 12px", borderRadius: "8px", border: "none", background: "#0f766e", color: "white", cursor: "pointer" }}
                  >
                    Assign Case
                  </button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                  <div style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                    <div style={{ fontWeight: 700, marginBottom: "6px" }}>Request Info</div>
                    <textarea
                      value={requestInfoReason}
                      onChange={(e) => setRequestInfoReason(e.target.value)}
                      placeholder="Why more information is needed"
                      style={{ width: "100%", minHeight: "70px", padding: "8px", borderRadius: "8px", border: "1px solid #cbd5e1", marginBottom: "8px", boxSizing: "border-box" }}
                    />
                    <button
                      disabled={saving || !requestInfoReason.trim()}
                      onClick={() =>
                        runAction(() =>
                          axios.put(
                            `${API_BASE_URL}/api/product-returns/ops/cases/${selectedCaseId}/request-info`,
                            { reason: requestInfoReason.trim() },
                            { headers: tokenHeader() },
                          ),
                        )
                      }
                      style={{ padding: "8px 12px", borderRadius: "8px", border: "none", background: "#b45309", color: "white", cursor: "pointer" }}
                    >
                      Request Info
                    </button>
                  </div>

                  <div style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                    <div style={{ fontWeight: 700, marginBottom: "6px" }}>Escalate</div>
                    <textarea
                      value={escalateReason}
                      onChange={(e) => setEscalateReason(e.target.value)}
                      placeholder="Escalation reason"
                      style={{ width: "100%", minHeight: "70px", padding: "8px", borderRadius: "8px", border: "1px solid #cbd5e1", marginBottom: "8px", boxSizing: "border-box" }}
                    />
                    <button
                      disabled={saving}
                      onClick={() =>
                        runAction(() =>
                          axios.put(
                            `${API_BASE_URL}/api/product-returns/ops/cases/${selectedCaseId}/escalate`,
                            { reason: escalateReason.trim() || "Escalated for governance review" },
                            { headers: tokenHeader() },
                          ),
                        )
                      }
                      style={{ padding: "8px 12px", borderRadius: "8px", border: "none", background: "#334155", color: "white", cursor: "pointer" }}
                    >
                      Escalate
                    </button>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
                  <div style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                    <div style={{ fontWeight: 700, marginBottom: "6px" }}>Decision</div>
                    <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                      <select
                        value={decisionForm.decision}
                        onChange={(e) => setDecisionForm((prev) => ({ ...prev, decision: e.target.value }))}
                        style={{ padding: "8px", borderRadius: "8px", border: "1px solid #cbd5e1" }}
                      >
                        <option value="approve">approve</option>
                        <option value="deny">deny</option>
                      </select>
                      <input
                        value={decisionForm.amountApproved}
                        onChange={(e) => setDecisionForm((prev) => ({ ...prev, amountApproved: e.target.value }))}
                        placeholder="Amount approved (optional)"
                        style={{ flex: 1, padding: "8px", borderRadius: "8px", border: "1px solid #cbd5e1" }}
                      />
                    </div>
                    <textarea
                      value={decisionForm.reason}
                      onChange={(e) => setDecisionForm((prev) => ({ ...prev, reason: e.target.value }))}
                      placeholder="Decision reason (required)"
                      style={{ width: "100%", minHeight: "70px", padding: "8px", borderRadius: "8px", border: "1px solid #cbd5e1", marginBottom: "8px", boxSizing: "border-box" }}
                    />
                    <button
                      disabled={saving || !decisionForm.reason.trim()}
                      onClick={() =>
                        runAction(() =>
                          axios.put(
                            `${API_BASE_URL}/api/product-returns/ops/cases/${selectedCaseId}/decision`,
                            {
                              decision: decisionForm.decision,
                              reason: decisionForm.reason.trim(),
                              amountApproved: decisionForm.amountApproved,
                            },
                            { headers: tokenHeader() },
                          ),
                        )
                      }
                      style={{ padding: "8px 12px", borderRadius: "8px", border: "none", background: "#2563eb", color: "white", cursor: "pointer" }}
                    >
                      Apply Decision
                    </button>
                  </div>

                  <div style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                    <div style={{ fontWeight: 700, marginBottom: "6px" }}>Execute Refund</div>
                    <input
                      value={refundForm.amount}
                      onChange={(e) => setRefundForm((prev) => ({ ...prev, amount: e.target.value }))}
                      placeholder="Refund amount"
                      style={{ width: "100%", padding: "8px", borderRadius: "8px", border: "1px solid #cbd5e1", marginBottom: "8px", boxSizing: "border-box" }}
                    />
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", fontSize: "13px" }}>
                      <input
                        type="checkbox"
                        checked={refundForm.simulate}
                        onChange={(e) => setRefundForm((prev) => ({ ...prev, simulate: e.target.checked }))}
                      />
                      Simulation mode (no Stripe transfer)
                    </label>
                    <button
                      disabled={saving || selectedCase.status !== "approved_for_refund"}
                      onClick={() =>
                        runAction(() =>
                          axios.put(
                            `${API_BASE_URL}/api/product-returns/ops/cases/${selectedCaseId}/execute-refund`,
                            {
                              amount: refundForm.amount || selectedCaseAmount,
                              simulate: refundForm.simulate,
                            },
                            { headers: tokenHeader() },
                          ),
                        )
                      }
                      style={{ padding: "8px 12px", borderRadius: "8px", border: "none", background: selectedCase.status === "approved_for_refund" ? "#15803d" : "#9ca3af", color: "white", cursor: selectedCase.status === "approved_for_refund" ? "pointer" : "not-allowed" }}
                    >
                      Execute Refund
                    </button>
                    {selectedCase.status !== "approved_for_refund" && (
                      <div style={{ marginTop: "6px", fontSize: "12px", color: "#64748b" }}>
                        Case must be approved_for_refund before execution.
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#fff" }}>
                  <div style={{ fontWeight: 700, marginBottom: "8px" }}>Audit Timeline</div>
                  {auditRows.length === 0 ? (
                    <div style={{ color: "#64748b" }}>No audit entries.</div>
                  ) : (
                    <div style={{ maxHeight: "240px", overflow: "auto" }}>
                      {auditRows.map((row) => (
                        <div key={row._id} style={{ borderTop: "1px solid #e2e8f0", paddingTop: "8px", marginTop: "8px" }}>
                          <div style={{ fontSize: "13px", fontWeight: 600 }}>
                            {String(row.actionType || "event").replace(/_/g, " ")}
                          </div>
                          <div style={{ fontSize: "12px", color: "#475569" }}>
                            {formatDateTime(row.createdAt)} | {row.actorRoleCode || "N/A"}
                          </div>
                          {row.reason ? (
                            <div style={{ fontSize: "12px", color: "#334155" }}>{row.reason}</div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </PortalAdminLayout>
  );
}

export default ProductReturnsOpsConsole;
